import { useMemo } from 'react';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  useIsMobile,
} from '@hotelise/ui';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Cell } from 'recharts';
import type { MergedGuest } from './guestUtils';

interface Props {
  guests: MergedGuest[];
}

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MUTED_COLOR = 'hsl(215, 15%, 65%)';
const PEAK_COLOR = 'hsl(210, 80%, 55%)';

export default function PeakArrivalDays({ guests }: Props) {
  const isMobile = useIsMobile();

  const { chartData, peakDay } = useMemo(() => {
    // Count check-ins per day of week (JS: 0=Sun, 1=Mon, ..., 6=Sat)
    const counts = [0, 0, 0, 0, 0, 0, 0]; // Mon–Sun indexed 0–6

    guests.forEach((g) => {
      g.reservations.forEach((r) => {
        const jsDay = r.checkin.getDay(); // 0=Sun, 1=Mon, ... 6=Sat
        // Remap to Mon=0 ... Sun=6
        const dayIdx = jsDay === 0 ? 6 : jsDay - 1;
        counts[dayIdx] += 1;
      });
    });

    const data = DAYS.map((day, i) => ({
      day,
      checkins: counts[i],
    }));

    // Find the peak day index (-1 if no check-ins)
    const totalCheckins = counts.reduce((s, c) => s + c, 0);
    let maxIdx = -1;
    if (totalCheckins > 0) {
      maxIdx = 0;
      counts.forEach((c, i) => {
        if (c > counts[maxIdx]) maxIdx = i;
      });
    }

    return { chartData: data, peakDay: maxIdx };
  }, [guests]);

  const chartConfig = {
    checkins: { label: 'Check-ins', color: PEAK_COLOR },
  };

  return (
    <div className="bg-card rounded-lg border p-4 md:p-6 overflow-hidden min-w-0">
      <div className="mb-4">
        <h2 className="text-sm font-semibold">Peak Arrival Days</h2>
        <p className="text-xs text-muted-foreground">Check-in distribution (Mon\u2013Sun)</p>
      </div>

      <ChartContainer config={chartConfig} className={`${isMobile ? 'h-[200px]' : 'h-[260px]'} w-full [&>div]:!aspect-auto`}>
        <BarChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
          <XAxis dataKey="day" tick={{ fontSize: isMobile ? 10 : 12 }} />
          <YAxis allowDecimals={false} tick={{ fontSize: isMobile ? 10 : 12 }} width={isMobile ? 25 : 35} />
          <ChartTooltip content={<ChartTooltipContent hideLabel={false} />} />
          <Bar dataKey="checkins" radius={[4, 4, 0, 0]}>
            {chartData.map((_, i) => (
              <Cell
                key={DAYS[i]}
                fill={i === peakDay ? PEAK_COLOR : MUTED_COLOR}
                fillOpacity={i === peakDay ? 1 : 0.4}
              />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  );
}
